/**
 * Adapter: builds a NavigationConfig for the Get Certified dropdown from BrowseNavStore data.
 * Content is loaded asynchronously via fetchChildren when the menu is shown (no pre-load guard).
 * Includes Exam Vouchers as the first section (L1) with provider-grouped vouchers loaded on hover (L2).
 */

import React from 'react';
import { BrowseNavigationItemSelected, BrowseNavigationItemStyle, BrowseNavigationItemType } from '@udemy/browse-event-tracking';
import { Tracker } from '@udemy/event-tracking';
import { ALL_CERTIFICATION_EXAM_VOUCHERS_URL, fetchExamVouchersAsBrowseNavItems } from '../../api/fetch-exam-vouchers';
import styles from "../../components/explore-menu.module.css";
import { BlockListLoader } from '../../components/explore-menu.react-component';
import { BrowseNavItemType } from '../../types/navigation';
/**
 * Returns a loading element for a Get Certified section (BlockList skeleton).
 * Config supplies this via renderLoadingState so ExploreMenu is not altered.
 */
function getSectionLoadingElement() {
  return /*#__PURE__*/React.createElement(BlockListLoader, {
    itemCount: 8,
    dataTestId: "get-certified-nav-level-two"
  });
}

/**
 * Returns a loading element for the Exam Vouchers submenu (fewer items, compact).
 */
function getExamVouchersLoadingElement() {
  return /*#__PURE__*/React.createElement(BlockListLoader, {
    itemCount: 3,
    dataTestId: "exam-vouchers-nav-level-two"
  });
}

/**
 * Optional callbacks for Get Certified nav (e.g. tracking when Exam Vouchers is clicked).
 */

/**
 * Builds the Exam Vouchers section (L1). Contains a single item that links to
 * /all-certification-vouchers/ and lazily loads provider-grouped vouchers on hover (L2).
 */
function buildExamVouchersSection(gettext, callbacks) {
  const examVouchersItem = {
    id: 'exam-vouchers',
    type: BrowseNavItemType.ITEM,
    title: gettext('Exam Vouchers'),
    onClick: callbacks === null || callbacks === void 0 ? void 0 : callbacks.onExamVouchersClick,
    fetchChildren: async () => {
      const providerItems = await fetchExamVouchersAsBrowseNavItems();
      const trackedProviderItems = providerItems.map(item => ({
        ...item,
        onClick: () => {
          Tracker.publishEvent(new BrowseNavigationItemSelected({
            id: item.id,
            type: BrowseNavigationItemType.CERTIFICATION_VOUCHER,
            uiRegion: 'main_nav',
            style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN
          }));
        }
      }));
      return [{
        id: 'exam-voucher-providers',
        type: BrowseNavItemType.SECTION,
        children: [...trackedProviderItems, {
          id: 'all-vouchers',
          type: BrowseNavItemType.ITEM,
          title: gettext('All Certification Exam Vouchers'),
          url: ALL_CERTIFICATION_EXAM_VOUCHERS_URL,
          metadata: {
            className: styles['all-elements-link']
          },
          onClick: () => {
            Tracker.publishEvent(new BrowseNavigationItemSelected({
              id: 'all-certification-vouchers',
              type: BrowseNavigationItemType.ALL_CERTIFICATION_VOUCHERS,
              uiRegion: 'main_nav',
              style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN
            }));
          }
        }]
      }];
    },
    renderLoadingState: () => getExamVouchersLoadingElement()
  };
  return {
    id: 'exam-vouchers-section',
    type: BrowseNavItemType.SECTION,
    sectionTitle: '',
    children: [examVouchersItem]
  };
}

/**
 * Maps a BrowseNavCategory (browse-nav-item) to ExploreMenu BrowseNavItem (navigation types).
 * Uses string ids and flattens title to string for BlockList.
 */
function mapCategoryToNavItem(category, prefix, type) {
  var _category$title;
  const title = typeof category.title === 'string' ? category.title : String((_category$title = category.title) !== null && _category$title !== void 0 ? _category$title : '');
  return {
    id: `${prefix}-${category.id}`,
    type,
    title,
    url: category.absolute_url,
    children: mapTopicChildren(category.children, prefix, category)
  };
}

/** Allowed relatedEntityType values for Get Certified (parent is always issuer or subject). */
const GET_CERTIFIED_RELATED_ENTITY_TYPES = new Set([BrowseNavigationItemType.CERTIFICATION_ISSUER_AGGREGATOR, BrowseNavigationItemType.CERTIFICATION_SUBJECT_AGGREGATOR]);

/**
 * Returns the parent category's type for event tracking only when it matches a known enum value.
 * Avoids casting at the call site; the Set membership check ensures runtime safety.
 */
function getRelatedEntityTypeForEvent(type) {
  return GET_CERTIFIED_RELATED_ENTITY_TYPES.has(type) ? type : undefined;
}

/**
 * Maps topic children (SDTag-like) to ExploreMenu items. Topics have no nested children in Get Certified.
 * Uses parent category so topic ids are unique and each topic gets an onClick that fires
 * BrowseNavigationItemSelected for parity with the legacy browse nav store.
 */
function mapTopicChildren(children, prefix, parentCategory) {
  if (!children || children.length === 0) return undefined;
  const parentPrefix = `${prefix}-${parentCategory.id}`;
  const relatedEntityType = getRelatedEntityTypeForEvent(parentCategory.type);
  return children.map(child => ({
    ...mapCategoryToNavItem(child, parentPrefix, BrowseNavItemType.TOPIC),
    onClick: () => {
      Tracker.publishEvent(new BrowseNavigationItemSelected({
        id: String(child.id),
        type: BrowseNavigationItemType.TOPIC,
        relatedEntityId: parentCategory.id,
        relatedEntityType,
        uiRegion: 'main_nav',
        style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN
      }));
    }
  }));
}

/**
 * Builds the two child sections (Popular Issuers, Popular Subjects) from current store state.
 * Used by fetchChildren after loadNavigationAggregatorTopics() has run.
 * Each issuer/subject item gets an onClick that fires BrowseNavigationItemSelected for parity
 * with the legacy browse nav store and mobile aggregator nav.
 */
function buildIssuersAndSubjectsSections(browseNavStore, gettext) {
  var _browseNavStore$aggre, _browseNavStore$aggre2;
  const issuers = (_browseNavStore$aggre = browseNavStore.aggregatorIssuers) !== null && _browseNavStore$aggre !== void 0 ? _browseNavStore$aggre : [];
  const subjects = (_browseNavStore$aggre2 = browseNavStore.aggregatorSubjects) !== null && _browseNavStore$aggre2 !== void 0 ? _browseNavStore$aggre2 : [];
  const issuersSection = {
    id: 'get-certified-issuers',
    type: BrowseNavItemType.SECTION,
    sectionTitle: gettext('Popular Issuers'),
    children: issuers.map(item => ({
      ...mapCategoryToNavItem(item, 'issuer', BrowseNavItemType.CATEGORY),
      onClick: () => {
        Tracker.publishEvent(new BrowseNavigationItemSelected({
          id: String(item.id),
          type: item.type,
          uiRegion: 'main_nav',
          style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN
        }));
      }
    }))
  };
  const subjectsSection = {
    id: 'get-certified-subjects',
    type: BrowseNavItemType.SECTION,
    sectionTitle: gettext('Popular Subjects'),
    children: subjects.map(item => ({
      ...mapCategoryToNavItem(item, 'subject', BrowseNavItemType.CATEGORY),
      onClick: () => {
        Tracker.publishEvent(new BrowseNavigationItemSelected({
          id: String(item.id),
          type: item.type,
          uiRegion: 'main_nav',
          style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN
        }));
      }
    }))
  };
  return [issuersSection, subjectsSection];
}

/**
 * Creates a NavigationConfig for the Get Certified dropdown.
 * When the store already has data, returns full structure so content shows without async.
 * When the store is empty, returns section stubs with fetchChildren so content loads when the menu is shown.
 * @param callbacks - Optional callbacks (e.g. onExamVouchersClick for tracking).
 */
export function getCertifiedNavigationConfig(browseNavStore, gettext, callbacks) {
  const allCertificationsNavElement = isSubjects => ({
    id: `all-certifications-${isSubjects ? 'subjects' : 'issuers'}`,
    type: BrowseNavItemType.CATEGORY,
    title: isSubjects ? gettext('All Certification Subjects') : gettext('All Certification Issuers'),
    url: '/browse/certification/',
    metadata: {
      className: styles['all-elements-link']
    },
    onClick: () => {
      Tracker.publishEvent(new BrowseNavigationItemSelected({
        id: isSubjects ? '-1' : '0',
        type: BrowseNavigationItemType.CERTIFICATION_PREPARATION,
        uiRegion: 'main_nav',
        style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN
      }));
    }
  });
  return {
    getInitialStructure() {
      var _browseNavStore$aggre3, _browseNavStore$aggre4, _browseNavStore$aggre5, _browseNavStore$aggre6;
      const hasData = ((_browseNavStore$aggre3 = (_browseNavStore$aggre4 = browseNavStore.aggregatorIssuers) === null || _browseNavStore$aggre4 === void 0 ? void 0 : _browseNavStore$aggre4.length) !== null && _browseNavStore$aggre3 !== void 0 ? _browseNavStore$aggre3 : 0) > 0 || ((_browseNavStore$aggre5 = (_browseNavStore$aggre6 = browseNavStore.aggregatorSubjects) === null || _browseNavStore$aggre6 === void 0 ? void 0 : _browseNavStore$aggre6.length) !== null && _browseNavStore$aggre5 !== void 0 ? _browseNavStore$aggre5 : 0) > 0;
      if (hasData) {
        const [issuersSection, subjectsSection] = buildIssuersAndSubjectsSections(browseNavStore, gettext);
        return {
          id: 'get-certified-root',
          type: BrowseNavItemType.SECTION,
          sectionTitle: '',
          children: [buildExamVouchersSection(gettext, callbacks), issuersSection, subjectsSection]
        };
      }
      const issuersSection = {
        id: 'get-certified-issuers',
        type: BrowseNavItemType.SECTION,
        sectionTitle: gettext('Popular Issuers'),
        children: undefined,
        fetchChildren: async () => {
          var _filled$children;
          await browseNavStore.loadNavigationAggregatorTopics();
          const [filled] = buildIssuersAndSubjectsSections(browseNavStore, gettext);
          (_filled$children = filled.children) === null || _filled$children === void 0 || _filled$children.push(allCertificationsNavElement(false));
          return [filled];
        },
        renderLoadingState: () => getSectionLoadingElement()
      };
      const subjectsSection = {
        id: 'get-certified-subjects',
        type: BrowseNavItemType.SECTION,
        sectionTitle: gettext('Popular Subjects'),
        children: undefined,
        fetchChildren: async () => {
          var _filled$children2;
          await browseNavStore.loadNavigationAggregatorTopics();
          const [, filled] = buildIssuersAndSubjectsSections(browseNavStore, gettext);
          (_filled$children2 = filled.children) === null || _filled$children2 === void 0 || _filled$children2.push(allCertificationsNavElement(true));
          return [filled];
        },
        renderLoadingState: () => getSectionLoadingElement()
      };
      return {
        id: 'get-certified-root',
        type: BrowseNavItemType.SECTION,
        sectionTitle: '',
        children: [buildExamVouchersSection(gettext, callbacks), issuersSection, subjectsSection]
      };
    }
  };
}
//# sourceMappingURL=get-certified-nav-config.js.map